/**
 * SequenceDetail - Gerenciar passos de uma sequência
 * Acessível via /sequences/:id
 */

import { useState } from "react";
import { useRoute, useLocation } from "wouter";
import DashboardLayout from "@/components/DashboardLayout";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Clock,
  MessageSquare,
  ListOrdered,
  Play,
  Pause,
  Save,
  GripVertical,
} from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDelay(days: number, hours: number): string {
  if (days === 0 && hours === 0) return "Imediato";
  const parts: string[] = [];
  if (days > 0) parts.push(`${days} dia${days !== 1 ? "s" : ""}`);
  if (hours > 0) parts.push(`${hours} hora${hours !== 1 ? "s" : ""}`);
  return parts.join(" e ");
}

// ─── Formulário inline de novo passo ─────────────────────────────────────────

function AddStepForm({
  sequenceId,
  nextOrder,
  templates,
  onDone,
}: {
  sequenceId: number;
  nextOrder: number;
  templates: any[];
  onDone: () => void;
}) {
  const utils = trpc.useUtils();
  const [form, setForm] = useState({
    templateId: "" as string,
    messageContent: "",
    delayDays: 0,
    delayHours: 0,
  });

  const addMutation = trpc.sequences.addStep.useMutation({
    onSuccess: () => {
      utils.sequences.getSteps.invalidate({ sequenceId });
      toast.success("Passo adicionado!");
      onDone();
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const handleAdd = () => {
    if (!form.templateId && !form.messageContent.trim()) {
      toast.error("Selecione um template ou escreva uma mensagem");
      return;
    }
    addMutation.mutate({
      sequenceId,
      stepOrder: nextOrder,
      templateId: form.templateId ? Number(form.templateId) : undefined,
      messageContent: form.messageContent.trim() || undefined,
      delayDays: form.delayDays,
      delayHours: form.delayHours,
    });
  };

  return (
    <Card className="border-primary/30 bg-primary/5">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Novo Passo #{nextOrder}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium block mb-1">Aguardar antes de enviar</label>
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  type="number"
                  min={0}
                  placeholder="Dias"
                  value={form.delayDays}
                  onChange={(e) => setForm({ ...form, delayDays: Number(e.target.value) })}
                />
                <p className="text-xs text-muted-foreground mt-1">Dias</p>
              </div>
              <div className="flex-1">
                <Input
                  type="number"
                  min={0}
                  max={23}
                  placeholder="Horas"
                  value={form.delayHours}
                  onChange={(e) => setForm({ ...form, delayHours: Number(e.target.value) })}
                />
                <p className="text-xs text-muted-foreground mt-1">Horas</p>
              </div>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium block mb-1">Template WhatsApp (opcional)</label>
            <Select
              value={form.templateId || "_none"}
              onValueChange={(v) => setForm({ ...form, templateId: v === "_none" ? "" : v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Nenhum template" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">Nenhum template</SelectItem>
                {templates.map((t: any) => (
                  <SelectItem key={t.id} value={t.id.toString()}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium block mb-1">Mensagem de texto (opcional)</label>
          <Textarea
            rows={3}
            placeholder="Ou escreva uma mensagem personalizada..."
            value={form.messageContent}
            onChange={(e) => setForm({ ...form, messageContent: e.target.value })}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Use se não for usar template. Variáveis: {"{"}{"{"} name {"}"}{"}"}, {"{"}{"{"} phone {"}"}{"}"}
          </p>
        </div>

        <div className="flex gap-2">
          <Button onClick={handleAdd} disabled={addMutation.isPending} className="gap-2">
            <Save className="w-4 h-4" />
            {addMutation.isPending ? "Adicionando..." : "Adicionar Passo"}
          </Button>
          <Button variant="outline" onClick={onDone}>Cancelar</Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Sortable Step Card ───────────────────────────────────────────────────────

function SortableStepCard({
  step,
  idx,
  templateMap,
  onDelete,
}: {
  step: any;
  idx: number;
  templateMap: Record<number, string>;
  onDelete: (id: number) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: step.id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <div ref={setNodeRef} style={style}>
      <Card className="border-border/50">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-start gap-4">
            {/* Drag handle + order */}
            <div className="flex flex-col items-center gap-1 shrink-0 pt-0.5">
              <button
                {...listeners}
                {...attributes}
                className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-muted text-muted-foreground"
                title="Arrastar para reordenar"
              >
                <GripVertical className="w-4 h-4" />
              </button>
              <div className="text-center text-xs font-bold text-muted-foreground px-1">
                {idx + 1}
              </div>
            </div>

            {/* Step info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <Badge variant="outline" className="text-xs gap-1">
                  <Clock className="w-3 h-3" />
                  {formatDelay(step.delayDays ?? 0, step.delayHours ?? 0)}
                </Badge>
                {step.templateId && (
                  <Badge variant="secondary" className="text-xs gap-1">
                    <MessageSquare className="w-3 h-3" />
                    Template: {templateMap[step.templateId] || `#${step.templateId}`}
                  </Badge>
                )}
              </div>

              {step.messageContent && (
                <p className="text-sm text-muted-foreground line-clamp-2 bg-muted/40 rounded px-3 py-2">
                  {step.messageContent}
                </p>
              )}

              {!step.templateId && !step.messageContent && (
                <p className="text-sm text-muted-foreground/60 italic">Sem conteúdo configurado</p>
              )}
            </div>

            {/* Delete */}
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0"
              onClick={() => onDelete(step.id)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Página Principal ─────────────────────────────────────────────────────────

export default function SequenceDetail() {
  const [, params] = useRoute("/sequences/:id");
  const [, navigate] = useLocation();
  const sequenceId = params?.id ? Number(params.id) : null;

  const [showAddForm, setShowAddForm] = useState(false);
  const [deleteStepId, setDeleteStepId] = useState<number | null>(null);

  const utils = trpc.useUtils();

  const { data: sequence, isLoading: seqLoading } = trpc.sequences.get.useQuery(
    { id: sequenceId! },
    { enabled: !!sequenceId }
  );
  const { data: steps = [], isLoading: stepsLoading } = trpc.sequences.getSteps.useQuery(
    { sequenceId: sequenceId! },
    { enabled: !!sequenceId }
  );
  const { data: templates = [] } = trpc.templates.list.useQuery();

  const updateMutation = trpc.sequences.update.useMutation({
    onSuccess: () => utils.sequences.get.invalidate({ id: sequenceId! }),
  });

  const updateStepMutation = trpc.sequences.updateStep.useMutation({
    onSuccess: () => utils.sequences.getSteps.invalidate({ sequenceId: sequenceId! }),
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const deleteStepMutation = trpc.sequences.deleteStep.useMutation({
    onSuccess: () => {
      utils.sequences.getSteps.invalidate({ sequenceId: sequenceId! });
      setDeleteStepId(null);
      toast.success("Passo removido!");
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const sortedSteps = [...steps].sort((a: any, b: any) => a.stepOrder - b.stepOrder);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sortedSteps.findIndex((s: any) => s.id === active.id);
    const newIndex = sortedSteps.findIndex((s: any) => s.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const reordered = arrayMove(sortedSteps, oldIndex, newIndex);
    reordered.forEach((step: any, idx: number) => {
      const newOrder = idx + 1;
      if (step.stepOrder !== newOrder) {
        updateStepMutation.mutate({ id: step.id, stepOrder: newOrder });
      }
    });
  };

  const toggleSequenceStatus = () => {
    if (!sequence) return;
    updateMutation.mutate({
      id: sequenceId!,
      status: sequence.status === "active" ? "inactive" : "active",
    });
  };

  if (!sequenceId) {
    return <DashboardLayout><div className="p-6">Sequência não encontrada.</div></DashboardLayout>;
  }

  if (seqLoading || stepsLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (!sequence) {
    return <DashboardLayout><div className="p-6">Sequência não encontrada.</div></DashboardLayout>;
  }

  const templateMap: Record<number, string> = {};
  templates.forEach((t: any) => { templateMap[t.id] = t.name; });

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/sequences")} className="gap-1.5">
              <ArrowLeft className="w-4 h-4" />
              Sequências
            </Button>
            <div className="h-4 w-px bg-border" />
            <div>
              <h1 className="text-2xl font-bold">{sequence.name}</h1>
              {sequence.description && (
                <p className="text-sm text-muted-foreground mt-0.5">{sequence.description}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Badge
              variant="outline"
              className={
                sequence.status === "active"
                  ? "bg-green-100 text-green-800 border-green-200"
                  : "bg-gray-100 text-gray-600 border-gray-200"
              }
            >
              {sequence.status === "active" ? "Ativa" : "Inativa"}
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={toggleSequenceStatus}
              className={
                sequence.status === "active"
                  ? "text-orange-600 hover:text-orange-700"
                  : "text-green-600 hover:text-green-700"
              }
            >
              {sequence.status === "active" ? (
                <><Pause className="w-4 h-4 mr-1" /> Pausar</>
              ) : (
                <><Play className="w-4 h-4 mr-1" /> Ativar</>
              )}
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 max-w-sm">
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-2">
                <ListOrdered className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="text-xl font-bold">{steps.length}</p>
                  <p className="text-xs text-muted-foreground">Passos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                <div>
                  <p className="text-xs font-medium">Gatilho</p>
                  <p className="text-xs text-muted-foreground capitalize">
                    {sequence.triggerType?.replace("_", " ") || "Manual"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Steps list */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Passos da Sequência</h2>
            {!showAddForm && (
              <Button onClick={() => setShowAddForm(true)} size="sm" className="gap-2">
                <Plus className="w-4 h-4" />
                Adicionar Passo
              </Button>
            )}
          </div>

          {sortedSteps.length === 0 && !showAddForm && (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <MessageSquare className="w-10 h-10 mx-auto mb-3 text-muted-foreground/30" />
                <p className="font-medium text-muted-foreground">Nenhum passo configurado</p>
                <p className="text-sm text-muted-foreground/70 mt-1">
                  Adicione passos para definir as mensagens que serão enviadas
                </p>
                <Button onClick={() => setShowAddForm(true)} className="mt-4 gap-2" variant="outline">
                  <Plus className="w-4 h-4" />
                  Adicionar primeiro passo
                </Button>
              </CardContent>
            </Card>
          )}

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={sortedSteps.map((s: any) => s.id)} strategy={verticalListSortingStrategy}>
              {sortedSteps.map((step: any, idx: number) => (
                <SortableStepCard
                  key={step.id}
                  step={step}
                  idx={idx}
                  templateMap={templateMap}
                  onDelete={setDeleteStepId}
                />
              ))}
            </SortableContext>
          </DndContext>

          {/* Add step inline form */}
          {showAddForm && (
            <AddStepForm
              sequenceId={sequenceId}
              nextOrder={sortedSteps.length + 1}
              templates={templates}
              onDone={() => setShowAddForm(false)}
            />
          )}

          {/* Button at bottom too if there are steps already */}
          {sortedSteps.length > 0 && !showAddForm && (
            <Button onClick={() => setShowAddForm(true)} variant="outline" className="w-full gap-2">
              <Plus className="w-4 h-4" />
              Adicionar Passo
            </Button>
          )}
        </div>
      </div>

      {/* Delete step confirmation */}
      <AlertDialog open={deleteStepId !== null} onOpenChange={() => setDeleteStepId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover Passo</AlertDialogTitle>
            <AlertDialogDescription>
              Este passo será removido permanentemente da sequência.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteStepId && deleteStepMutation.mutate({ id: deleteStepId })}
            >
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
